import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaTenCoordinatorV1Service } from '../service';
import { GetLocationsRegistrationStatusesIO } from '../types';

export const getGetLocationsRegistrationStatusesQueryKey = (req: GetLocationsRegistrationStatusesIO['input']) =>
  ['TenCoordinatorV1', 'GetLocationsRegistrationStatuses', req] as const;
type QueryKey = ReturnType<typeof getGetLocationsRegistrationStatusesQueryKey>;

/**
 * A hook that returns a query for the `GetLocationsRegistrationStatuses` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetLocationsRegistrationStatusesQuery = <E = unknown, D = GetLocationsRegistrationStatusesIO['output']>(
  req: GetLocationsRegistrationStatusesIO['input'],
  options?: LimitedSchemaQueryOptions<GetLocationsRegistrationStatusesIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetLocationsRegistrationStatusesIO['output'], E, D, QueryKey>({
    queryKey: getGetLocationsRegistrationStatusesQueryKey(req),
    queryFn: () => SchemaTenCoordinatorV1Service.GetLocationsRegistrationStatuses(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetLocationsRegistrationStatuses` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetLocationsRegistrationStatusesQueries = <
  E = unknown,
  D = GetLocationsRegistrationStatusesIO['output']
>(
  argsArr: {
    req: GetLocationsRegistrationStatusesIO['input'];
    options?: LimitedSchemaQueryOptions<GetLocationsRegistrationStatusesIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetLocationsRegistrationStatusesQueryKey(req),
      queryFn: () => SchemaTenCoordinatorV1Service.GetLocationsRegistrationStatuses(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetLocationsRegistrationStatusesQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetLocationsRegistrationStatuses` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetLocationsRegistrationStatuses = () => {
  const queryClient = useQueryClient();

  const fetchGetLocationsRegistrationStatuses = async <E = unknown, D = GetLocationsRegistrationStatusesIO['output']>(
    req: GetLocationsRegistrationStatusesIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetLocationsRegistrationStatusesIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetLocationsRegistrationStatusesIO['output'], E, D, QueryKey>(
      getGetLocationsRegistrationStatusesQueryKey(req),
      () => SchemaTenCoordinatorV1Service.GetLocationsRegistrationStatuses(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetLocationsRegistrationStatuses;
};
